import type { LngLatBoundsLike } from "@ui/components/Map";
import { useCallback, useMemo } from "react";
import { Layer, Marker, Source } from "react-map-gl/mapbox";

export interface BboxCoords {
  north: number;
  south: number;
  east: number;
  west: number;
}

export const ADMIN_MAP_CLASSNAME = "h-80 w-full overflow-hidden rounded-lg border border-primary";
export const ADMIN_MAP_BOUNDS_PADDING = 40;

interface LatLong {
  latitude: number;
  longitude: number;
}

export function bboxToEnvelope(bbox: BboxCoords, fitPoints?: LatLong[]): LngLatBoundsLike {
  const lats = [bbox.north, bbox.south];
  const lngs = [bbox.east, bbox.west];
  if (fitPoints) {
    for (const p of fitPoints) {
      lats.push(p.latitude);
      lngs.push(p.longitude);
    }
  }
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}

interface BboxRectangleProps {
  bbox: BboxCoords;
  editable?: boolean;
  onBboxChange?: (bbox: BboxCoords) => void;
}

export function BboxRectangle({ bbox, editable = false, onBboxChange }: BboxRectangleProps) {
  const geojson = useMemo(
    () => ({
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "Polygon" as const,
        coordinates: [
          [
            [bbox.west, bbox.south],
            [bbox.east, bbox.south],
            [bbox.east, bbox.north],
            [bbox.west, bbox.north],
            [bbox.west, bbox.south],
          ],
        ],
      },
    }),
    [bbox],
  );

  const handleCornerDrag = useCallback(
    (corner: "nw" | "ne" | "sw" | "se", event: { lngLat: { lng: number; lat: number } }) => {
      if (!onBboxChange) return;
      const { lng, lat } = event.lngLat;

      const updated = { ...bbox };
      if (corner.includes("n")) updated.north = lat;
      if (corner.includes("s")) updated.south = lat;
      if (corner.includes("e")) updated.east = lng;
      if (corner.includes("w")) updated.west = lng;

      onBboxChange(updated);
    },
    [bbox, onBboxChange],
  );

  const corners: Array<{ id: "nw" | "ne" | "sw" | "se"; lng: number; lat: number }> = [
    { id: "nw", lng: bbox.west, lat: bbox.north },
    { id: "ne", lng: bbox.east, lat: bbox.north },
    { id: "sw", lng: bbox.west, lat: bbox.south },
    { id: "se", lng: bbox.east, lat: bbox.south },
  ];

  return (
    <>
      <Source id="bbox" type="geojson" data={geojson}>
        <Layer
          id="bbox-fill"
          type="fill"
          paint={{
            "fill-color": "#3b82f6",
            "fill-opacity": 0.1,
          }}
        />
        <Layer
          id="bbox-line"
          type="line"
          paint={{
            "line-color": "#3b82f6",
            "line-width": 2,
            "line-dasharray": [2, 2],
          }}
        />
      </Source>

      {editable &&
        corners.map((corner) => (
          <Marker
            key={corner.id}
            longitude={corner.lng}
            latitude={corner.lat}
            draggable
            onDragEnd={(e) => handleCornerDrag(corner.id, e)}
          >
            <div className="size-3 cursor-move rounded-full border-2 border-blue-500 bg-white shadow" />
          </Marker>
        ))}
    </>
  );
}
